import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LATER, type LoadBand } from '@rundown/domain';
import type { ClientStatus, SprintState, WorkType } from '@rundown/types';
import {
  backlogQueryOptions,
  isInternalBacklogRow,
  projectionQueryOptions,
  type BacklogRow,
  type ProjectionResponse,
} from '../backlog/api';
import {
  deliverySprintsQueryOptions,
  isInternalSprintItem,
  planChangesQueryOptions,
  type DeliverySprint,
  type PlanChange,
} from '../sprints/api';
import {
  currentSprintId,
  loadInfo,
  localToday,
  timelineStates,
  type TimelineState,
} from '../sprints/sprintMeta';

/**
 * One reading of the plan, for one screen.
 *
 * There used to be two. `buildBoard` assembled columns for the Planning screen
 * and `useSprintTimeline` assembled sprints for the Sprints screen, from the
 * same four queries, and they classified the same items under two vocabularies
 * that meant the same things: `committed`/`placed` appeared in both, and
 * `projected` and `ranking` were one idea under two names. Two readings of one
 * dataset is two chances to disagree about what a card is, and the screens did.
 */

/**
 * Why a request is sitting in a sprint. The part a reader needs in order to
 * trust the column: agreed scope, a suggestion, or merely where today's
 * ranking would drop it.
 */
export type Mark =
  /** In the backlog, committed to nothing. */
  | 'backlog'
  /** Placed by us into a sprint still being planned. */
  | 'committed'
  /** In a locked sprint's frozen scope. */
  | 'agreed'
  /** Brought into a locked sprint by an agreed change of plan. */
  | 'added'
  /** Taken out of a locked sprint by an agreed change of plan. */
  | 'removed'
  /** Suggested by the client, not yet answered. */
  | 'placed'
  /** Nobody placed it; this is where the ranking lands it. */
  | 'projected'
  | 'delivered'
  /** In a released sprint, unfinished. */
  | 'carried';

export interface PlanCardModel {
  readonly requestId: string;
  readonly number: number;
  readonly title: string;
  readonly type: WorkType;
  readonly projectName: string | null;
  readonly approxHours: number;
  readonly status: ClientStatus;
  readonly ownerId: string | null;
  readonly ownerName: string | null;
  readonly closedAt: string | null;
  readonly removedAt: string | null;
  readonly mark: Mark;
  /** The sprint it is committed to or suggested for; null in the backlog. */
  readonly sprintId: string | null;
  /** Backlog cards only: where the ranking says it would land. */
  readonly landsIn?: string;
  readonly rank?: number;
}

export interface PlanColumn {
  readonly sprint: DeliverySprint;
  readonly state: TimelineState;
  readonly items: readonly PlanCardModel[];
  /** Agreed scope, planned load, or hours delivered — whichever the state makes true. */
  readonly hours: number;
  readonly band: LoadBand | null;
  readonly loadLabel: string;
  readonly width: number;
  readonly planChanges: readonly PlanChange[];
  /** Scope is fixed, so this column refuses drops. */
  readonly frozen: boolean;
}

export interface PlanBoardModel {
  readonly columns: readonly PlanColumn[];
  readonly backlog: readonly PlanCardModel[];
  readonly later: readonly PlanCardModel[];
  readonly laterHours: number;
  readonly currentId: string | null;
  readonly today: string;
  readonly projection: ProjectionResponse | undefined;
  readonly isPending: boolean;
  readonly error: Error | null;
}

/** A drop target meaning "out of every sprint". */
export const BACKLOG = 'backlog';

function cardFromBacklogRow(row: BacklogRow, mark: Mark, sprintId: string | null): PlanCardModel {
  return {
    requestId: row.id,
    number: row.number,
    title: row.title,
    type: row.type,
    projectName: row.projectName,
    approxHours: row.approxHours,
    // A backlog row carries no status of its own: by definition nothing has
    // happened to it yet, so it is whatever the review queue says.
    status: 'queued',
    ownerId: isInternalBacklogRow(row) ? row.ownerId : null,
    ownerName: row.ownerName,
    closedAt: null,
    removedAt: null,
    mark,
    sprintId,
  };
}

/**
 * @param pending Moves still in flight, so a dropped card sits where it was
 *   dropped rather than snapping back for the length of a round trip.
 * @param includePast Released sprints are normally left out. Dozens accumulate
 *   within months, and what shipped belongs on Completed; the toggle is for the
 *   times you want the last few beside what is coming.
 */
export function usePlanBoard(
  workspaceId: string,
  pending: ReadonlyMap<string, string>,
  includePast: boolean,
): PlanBoardModel {
  const deliveryQuery = useQuery(deliverySprintsQueryOptions(workspaceId));
  const projectionQuery = useQuery(projectionQueryOptions(workspaceId, 'team', 'strict-rank'));
  const backlogQuery = useQuery(backlogQueryOptions(workspaceId));
  const planChangesQuery = useQuery(planChangesQueryOptions(workspaceId));

  const today = localToday();

  const model = useMemo(() => {
    const delivery = deliveryQuery.data ?? [];
    const projection = projectionQuery.data;
    const backlogRows = backlogQuery.data ?? [];
    const changes = planChangesQuery.data ?? [];
    const states = timelineStates(delivery);

    const backlogById = new Map(backlogRows.map((row) => [row.id, row]));
    const sprintIdByKey = new Map((projection?.sprints ?? []).map((sprint) => [sprint.key, sprint.id]));
    const nameByKey = new Map((projection?.sprints ?? []).map((sprint) => [sprint.key, sprint.name]));
    const landingById = new Map((projection?.landings ?? []).map((landing) => [landing.itemId, landing]));

    const visible = includePast ? delivery : delivery.filter((sprint) => sprint.state !== 'released');

    const columns = visible.map((sprint): PlanColumn => {
      const state = states.get(sprint.id) ?? 'planning';
      const frozen = sprint.state !== 'planning';
      const planChanges = changes.filter((change) => change.sprintId === sprint.id);

      const items: PlanCardModel[] = sprint.items.map((item): PlanCardModel => {
        const internal = isInternalSprintItem(item) ? item : null;
        let mark: Mark;
        if (sprint.state === 'planning') mark = 'committed';
        else if (item.removedAt !== null) mark = 'removed';
        else if (sprint.state === 'released') mark = item.status === 'released' ? 'delivered' : 'carried';
        else mark = internal?.addedAfterLock === true ? 'added' : 'agreed';
        return {
          requestId: item.requestId,
          number: item.number,
          title: item.title,
          type: item.type,
          projectName: item.projectName,
          approxHours: item.approxHours,
          status: item.status,
          ownerId: internal?.ownerId ?? null,
          ownerName: item.ownerName,
          closedAt: item.closedAt,
          removedAt: item.removedAt,
          mark,
          sprintId: sprint.id,
        };
      });

      // A sprint still being planned also shows what the ranking would pull in,
      // which is the difference between "nothing is in this week" and "nothing
      // has been decided about this week".
      if (sprint.state === 'planning' && projection !== undefined) {
        for (const landing of projection.landings) {
          if (landing.sprint !== sprint.key) continue;
          const row = backlogById.get(landing.itemId);
          if (row === undefined) continue;
          items.push(
            cardFromBacklogRow(
              row,
              landing.reason === 'pinned' ? 'placed' : 'projected',
              landing.reason === 'pinned' ? sprint.id : null,
            ),
          );
        }
      }

      if (sprint.state === 'released') {
        const hours = items
          .filter((card) => card.mark === 'delivered')
          .reduce((total, card) => total + card.approxHours, 0);
        return {
          sprint,
          state,
          items,
          hours,
          band: null,
          loadLabel: 'Delivered',
          width: 100,
          planChanges,
          frozen,
        };
      }

      const hours =
        sprint.state === 'planning' && projection?.load[sprint.key] !== undefined
          ? projection.load[sprint.key]!
          : items
              .filter((card) => card.removedAt === null && card.mark !== 'projected')
              .reduce((total, card) => total + card.approxHours, 0);
      const load = loadInfo(hours, sprint.plannableHours);
      return {
        sprint,
        state,
        items,
        hours,
        band: load.band,
        loadLabel: load.label,
        width: load.width,
        planChanges,
        frozen,
      };
    });

    const backlog: PlanCardModel[] = [];
    const later: PlanCardModel[] = [];
    let laterHours = 0;

    backlogRows.forEach((row, index) => {
      // A client's suggestion is drawn in the sprint it was suggested for, not
      // in the backlog — the whole point of the pin is that it has a home.
      if (row.placedSprintId !== null) return;

      const landing = landingById.get(row.id);
      const landsIn =
        landing === undefined || landing.reason === 'unestimated'
          ? '' // the size already reads "Needs analysis"; a date would be a guess
          : landing.sprint === LATER
            ? 'later'
            : `projected ${nameByKey.get(landing.sprint) ?? landing.sprint}`;

      backlog.push({ ...cardFromBacklogRow(row, 'backlog', null), landsIn, rank: index + 1 });

      const landedSprintId =
        landing && landing.sprint !== LATER ? sprintIdByKey.get(landing.sprint) : undefined;
      if (landedSprintId === undefined && landing?.sprint === LATER && landing.reason !== 'unestimated') {
        later.push(cardFromBacklogRow(row, 'projected', null));
        laterHours += row.approxHours;
      }
    });

    return { columns, backlog, later, laterHours, projection };
  }, [deliveryQuery.data, projectionQuery.data, backlogQuery.data, planChangesQuery.data, includePast]);

  // Pending drops are applied outside the memo above so that a drag does not
  // rebuild the whole board: take the card out of wherever it was drawn and
  // put it where it was dropped.
  const withPending = useMemo(() => {
    if (pending.size === 0) return { columns: model.columns, backlog: model.backlog };

    const moved = new Map<string, PlanCardModel>();
    for (const column of model.columns) {
      for (const card of column.items) if (pending.has(card.requestId)) moved.set(card.requestId, card);
    }
    for (const card of model.backlog) if (pending.has(card.requestId)) moved.set(card.requestId, card);

    const columns = model.columns.map((column) => ({
      ...column,
      items: [
        ...column.items.filter((card) => !pending.has(card.requestId)),
        ...[...pending]
          .filter(([, target]) => target === column.sprint.id)
          .flatMap(([requestId]) => {
            const card = moved.get(requestId);
            return card === undefined
              ? []
              : [
                  {
                    ...card,
                    sprintId: column.sprint.id,
                    mark: card.mark === 'backlog' ? 'placed' : card.mark,
                  } as PlanCardModel,
                ];
          }),
      ],
    }));

    const backlog = [
      ...model.backlog.filter((card) => !pending.has(card.requestId)),
      ...[...pending]
        .filter(([, target]) => target === BACKLOG)
        .flatMap(([requestId]) => {
          const card = moved.get(requestId);
          return card === undefined ? [] : [{ ...card, sprintId: null, mark: 'backlog' } as PlanCardModel];
        }),
    ];

    return { columns, backlog };
  }, [model, pending]);

  return {
    columns: withPending.columns,
    backlog: withPending.backlog,
    later: model.later,
    laterHours: model.laterHours,
    currentId: currentSprintId(deliveryQuery.data ?? [], today),
    today,
    projection: model.projection,
    isPending: deliveryQuery.isPending || projectionQuery.isPending,
    // A partial board is worse than an honest failure: the projection failing
    // silently would draw a plan with nothing projected into it.
    error: deliveryQuery.error ?? projectionQuery.error ?? backlogQuery.error,
  };
}

export type { SprintState };
